<template lang="html">
  <section class="screen-transactions">
    <prt-section-separator name="Currency Preferences" />
    <prt-input :error="currencySymbol && currencySymbol.length > 1" placeholder="€" single-char v-model="currencySymbol">
      <span>Currency Symbol:</span>
    </prt-input>
    <prt-switch off="before" on="after" v-model="suffixSymbol">
      <span>Symbol Placement:</span>
    </prt-switch>
    <prt-section-separator name="Default Descriptions" />
    <prt-input
      :key="icon"
      placeholder="Description"
      v-for="icon in Object.keys($store.state.preferences.defaultDescriptions)"
      :value="$store.state.preferences.defaultDescriptions[icon]"
      @input="updateDefaultDescription($event, icon)"
    >
      <prt-icon :icon="icon" />
      <template v-slot:after>
        <prt-switch off="subtract" on="add" use-icons :value="$store.state.preferences.positiveDefaults.includes(icon)" @input="handlePositiveDefaultInput($event, icon)" />
      </template>
    </prt-input>
    <prt-section-separator name="Backup &amp; Restore" />
    <div class="button-wrapper">
      <prt-button icon-left="import" @click.native="importData">Import Data</prt-button>
      <prt-button icon-right="export" @click.native="exportData">Export Data</prt-button>
      <input accept=".json" ref="dataInput" style="display: none" type="file" @change="readData">
    </div>
    <prt-section-separator name="Information" />
    <div class="button-wrapper">
      <prt-button @click.native="$emit('show-modal', 'changelog')">Changelog</prt-button>
      <prt-button @click.native="$emit('show-modal', 'privacyPolicy')">Privacy Policy</prt-button>
    </div>
    <template v-if="installPrompt && userPrompted">
      <prt-section-separator name="Application" />
      <div class="button-wrapper">
        <prt-button @click.native="promptInstall">Install to Device</prt-button>
      </div>
    </template>
    <span class="credit">Made with 💚 by <a href="https://amxmln.com" target="_blank">Amadeus Maximilian</a></span>
  </section>
</template>

<script>
import { format } from 'date-fns';
import debounce from 'lodash.debounce';

import PrtButton from './ui/PrtButton.vue';
import PrtIcon from './ui/PrtIcon.vue';
import PrtInput from './ui/PrtInput.vue';
import PrtSectionSeparator from './ui/PrtSectionSeparator.vue';
import PrtSwitch from './ui/PrtSwitch.vue';

export default {
  components: {
    PrtButton,
    PrtIcon,
    PrtInput,
    PrtSectionSeparator,
    PrtSwitch,
  },
  computed: {
    currencySymbol: {
      get() {
        return this.$store.state.preferences.currencySymbol;
      },
      set: debounce(function (value) { // eslint-disable-line func-names
        this.$store.dispatch('updateCurrencySymbol', { value })
          .catch(err => this.$store.commit('addToast', { content: `Could not save preferences: ${err.message}`, color: 'negative', timeout: 5000 }));
      }, 500),
    },
    installPrompt() {
      return this.$store.state.application.installPrompt;
    },
    suffixSymbol: {
      get() {
        return this.$store.state.preferences.suffixCurrencySymbol;
      },
      set: debounce(function (value) { // eslint-disable-line func-names
        this.$store.dispatch('updateSuffixCurrencySymbol', { value })
          .catch(err => this.$store.commit('addToast', { content: `Could not save preferences: ${err.message}`, color: 'negative', timeout: 5000 }));
      }, 500, { leading: true }),
    },
    userPrompted() {
      return this.$store.state.user.prompted;
    },
  },
  methods: {
    exportData() {
      this.$store.dispatch('exportData')
        .then((data) => {
          const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
          const filename = `PrtMnyData-${format(new Date(), 'YYYY-MM-DD-HH-mm')}.json`;
          if (typeof window.navigator.msSaveBlob !== 'undefined') window.navigator.msSaveBlob(blob, filename);
          else {
            const tempLink = document.createElement('a');
            const blobUrl = window.URL.createObjectURL(blob);
            tempLink.style.display = 'none';
            tempLink.href = blobUrl;
            tempLink.setAttribute('download', filename);
            if (typeof tempLink.download === 'undefined') tempLink.setAttribute('target', '_blank');
            document.body.appendChild(tempLink);
            tempLink.click();
            document.body.removeChild(tempLink);
            if (blobUrl) window.URL.revokeObjectURL(blobUrl);
          }
        })
        .catch(err => this.$store.commit('addToast', { color: 'negative', content: `Could not export data: ${err.message}`, timeout: 5000 }));
    },
    handlePositiveDefaultInput(value, icon) {
      if (value) this.$store.dispatch('addPositiveDefault', icon);
      else this.$store.dispatch('removePositiveDefault', icon);
    },
    importData() {
      this.$refs.dataInput.click();
    },
    promptInstall() {
      this.installPrompt.prompt();
      this.installPrompt.userChoice
        .then((choice) => {
          if (choice.outcome === 'accepted') this.$store.commit('addToast', { content: 'Great! Thank you for your trust.', color: 'positive', timeout: 5000 });
          else this.$store.commit('addToast', { content: 'Should you change your mind, you can still install the application from the settings.', color: 'positive', timeout: 5000 });
          this.$store.commit('setInstallPrompt', null);
        });
    },
    readData(e) {
      this.$store.commit('setLoadingModal', 'Importing Data…');
      const fileReader = new FileReader();
      fileReader.readAsText(e.target.files[0]);
      fileReader.addEventListener('loadend', (evt) => {
        try {
          const data = JSON.parse(evt.target.result);
          this.$store.dispatch('importData', data)
            .then(() => {
              this.$store.dispatch('initializeState');
              this.$store.commit('setLoadingModal', '');
              this.$router.push('/');
            })
            .catch((err) => {
              this.$store.commit('setLoadingModal', '');
              this.$store.commit('addToast', { color: 'negative', content: `Could not import data: ${err.message}`, timeout: 5000 });
            });
        } catch (err) {
          this.$store.commit('setLoadingModal', '');
          this.$store.commit('addToast', { color: 'negative', content: 'Could not import data: File is invalid JSON', timeout: 5000 });
        }
      });
    },
    updateDefaultDescription: debounce(function (value, icon) { // eslint-disable-line func-names
      this.$store.dispatch('updateDefaultDescriptions', { field: icon, value })
        .catch(err => this.$store.commit('addToast', { content: `Could not save preferences: ${err.message}`, color: 'negative', timeout: 5000 }));
    }, 500),
  },
  name: 'Settings',
};
</script>

<style lang="stylus" scoped>
  @require '../styles/colors'

  .screen-transactions
    height: 100vh
    overflow-x: hidden
    overflow-y: auto
    margin: 0 auto

    .prt-input
      .prt-icon
        flex-shrink: 0

      .prt-switch
        margin: 0
        width: auto

    .prt-section-separator
      max-width: 1200px
      margin-left: auto
      margin-right: auto

    .button-wrapper
      margin: 16px auto
      text-align: center

      .prt-button
        margin-bottom: 0

    .credit
      display: block
      text-align: center
      margin: 16px auto 62px auto

</style>
